import { League, LeagueParticipant } from '../../domain/models';
import { LeagueRepository } from '../../domain/repositories';
import { LeagueDataSource } from '../datasources/LeagueDataSource';
import { leagueEntityToModel, leagueModelToEntity } from '../entities/LeagueEntity';
import { leagueParticipantEntityToModel } from '../entities/LeagueParticipantEntity';

export class LeagueRepositoryImpl implements LeagueRepository {
  constructor(private dataSource: LeagueDataSource) {}

  async create(league: Omit<League, 'id' | 'createdAt' | 'updatedAt'>): Promise<League> {
    const entity = leagueModelToEntity(league);
    const createdEntity = await this.dataSource.create(entity as any);
    return leagueEntityToModel(createdEntity);
  }

  async getById(id: string): Promise<League | null> {
    const entity = await this.dataSource.getById(id);
    if (!entity) return null;
    return leagueEntityToModel(entity);
  }

  async getUserLeagues(userId: string): Promise<League[]> {
    const entities = await this.dataSource.getUserLeagues(userId);
    return entities.map(leagueEntityToModel);
  }

  async getPublicLeagues(limit: number, offset: number): Promise<League[]> {
    const entities = await this.dataSource.getPublicLeagues(limit, offset);
    return entities.map(leagueEntityToModel);
  }

  async update(id: string, league: Partial<League>): Promise<League> {
    const entity = leagueModelToEntity(league);
    const updatedEntity = await this.dataSource.update(id, entity);
    return leagueEntityToModel(updatedEntity);
  }

  async markTournamentsPublic(leagueId: string): Promise<void> {
    await this.dataSource.markTournamentsPublic(leagueId);
  }

  async delete(id: string): Promise<void> {
    await this.dataSource.delete(id);
  }

  async addParticipant(participant: Omit<LeagueParticipant, 'id' | 'createdAt'>): Promise<LeagueParticipant> {
    const entity = {
      league_id: participant.leagueId,
      name: participant.name,
      user_id: participant.userId || null,
    };
    const createdEntity = await this.dataSource.addParticipant(entity);
    return leagueParticipantEntityToModel(createdEntity);
  }

  async getParticipants(leagueId: string): Promise<LeagueParticipant[]> {
    const entities = await this.dataSource.getParticipants(leagueId);
    return entities.map(leagueParticipantEntityToModel);
  }

  async removeParticipant(participantId: string): Promise<void> {
    await this.dataSource.removeParticipant(participantId);
  }
}
